import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';
import axiosInstance from '../../api';
import Swal from 'sweetalert2';
import { isSoc2, TSC_LABEL_KEYS } from '../../../utils/tsc';
import { isPlatformAdmin } from '../../../utils/platformRole';
import { startPeek } from '../../../utils/peek';
import './Organizations.css';

const EMPTY_FORM = {
  organizationName: '',
  address: '',
  orgWebsiteUrl: '',
  contactEmail: '',
  contactNumber: '',
  logoUrl: '',
  frameworks: [],
  testOrg: false,
};

function OrgAvatar({ org }) {
  if (org.logoUrl) {
    return <img src={org.logoUrl} alt="" className="org-logo" />;
  }
  return (
    <div className="org-avatar">
      {(org.organizationName || '?')[0].toUpperCase()}
    </div>
  );
}

function Organizations() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // Archiving/restoring an org is platform-admin ONLY (carve-out #1). platform_user can view and
  // manage orgs but must not remove them; hide the buttons (the API 403s the action regardless).
  const canDeleteOrg = isPlatformAdmin();
  // Tagging an org as a test tenant (payment-wall exemption) is platform-admin ONLY as well.
  const canTagTestOrg = isPlatformAdmin();
  const [organizations, setOrganizations] = useState([]);
  const [frameworks, setFrameworks]       = useState([]);
  const [fwModal, setFwModal]             = useState(null);   // { org } when open
  const [fwAdding, setFwAdding]           = useState(false);
  const [fwSelected, setFwSelected]       = useState(null);   // { value, label } to add
  const [tscChecked, setTscChecked]       = useState({});     // SOC 2 optional-pillar selection for fwSelected
  const [showModal, setShowModal]         = useState(false);
  const [form, setForm]                   = useState(EMPTY_FORM);
  const [fieldErr, setFieldErr]           = useState({});
  const [saving, setSaving]               = useState(false);
  const [search, setSearch]               = useState('');
  const [showArchived, setShowArchived]   = useState(false);
  const [emailModal, setEmailModal]       = useState(null); // { org, recipient, hasRecipient, subject, message, loading, sending }
  const [orgsLoading, setOrgsLoading]     = useState(true);
  const [orgsLoadError, setOrgsLoadError] = useState(false);
  const orgsFetchSeq = useRef(0);

  const fetchOrganizations = useCallback(async () => {
    const seq = ++orgsFetchSeq.current;
    setOrgsLoading(true);
    setOrgsLoadError(false);
    try {
      const res = await axiosInstance.get('/organizations', { params: { includeArchived: showArchived } });
      if (seq !== orgsFetchSeq.current) return; // a newer fetch already superseded this one
      setOrganizations(res.data);
    } catch (e) {
      console.error('Error fetching organizations:', e);
      if (seq !== orgsFetchSeq.current) return;
      setOrgsLoadError(true);
    } finally {
      if (seq === orgsFetchSeq.current) setOrgsLoading(false);
    }
  }, [showArchived]);

  const fetchFrameworks = useCallback(async () => {
    try {
      const res = await axiosInstance.get('/frameworks');
      setFrameworks(res.data.map(f => ({ value: f.id, label: f.displayName })));
    } catch (e) {
      console.error('Error fetching frameworks:', e);
    }
  }, []);

  useEffect(() => {
    fetchOrganizations();
    fetchFrameworks();
  }, [fetchOrganizations, fetchFrameworks]);

  const openModal  = () => { setForm(EMPTY_FORM); setFieldErr({}); setShowModal(true); };
  const closeModal = () => { setShowModal(false); setForm(EMPTY_FORM); setFieldErr({}); };

  const openFwModal  = (org) => { setFwModal({ org }); setFwSelected(null); setTscChecked({}); };
  const closeFwModal = () => { setFwModal(null); setFwSelected(null); setTscChecked({}); };

  // Same optional-pillar set as the self-serve enrollment picker (Frameworks.js) — CC is mandatory
  // and always included, these four are opt-in.
  const SOC2_PILLARS = [
    { code: 'A1',  labelKey: TSC_LABEL_KEYS.A1 },
    { code: 'C1',  labelKey: TSC_LABEL_KEYS.C1 },
    { code: 'PI1', labelKey: TSC_LABEL_KEYS.PI },
    { code: 'P',   labelKey: TSC_LABEL_KEYS.P },
  ];
  const fwSelectedIsSoc2 = fwSelected ? isSoc2({ displayName: fwSelected.label }) : false;

  const handleAddFramework = async () => {
    if (!fwSelected || !fwModal) return;
    setFwAdding(true);
    try {
      const tscSelection = fwSelectedIsSoc2
        ? ['CC', ...SOC2_PILLARS.filter(pl => tscChecked[pl.code]).map(pl => pl.code)].join(',')
        : null;
      await axiosInstance.post(`/admin/orgs/${fwModal.org.id}/frameworks/${fwSelected.value}`, {}, {
        params: tscSelection ? { tscSelection } : {},
      });
      await fetchOrganizations();
      setFwModal(prev => ({
        ...prev,
        org: { ...prev.org, frameworks: [...(prev.org.frameworks || []), { id: fwSelected.value, displayName: fwSelected.label }] }
      }));
      setFwSelected(null);
      setTscChecked({});
      Swal.fire({ toast: true, position: 'top-end', icon: 'success', title: t('Organizations.toastFrameworkAdded'), showConfirmButton: false, timer: 2000 });
    } catch (e) {
      Swal.fire(t('Organizations.errorTitle'), e.response?.data?.message || t('Organizations.errorAddFramework'), 'error');
    } finally {
      setFwAdding(false);
    }
  };

  const handleRemoveFramework = async (org, fw) => {
    const confirmed = await Swal.fire({
      title: t('Organizations.removeFrameworkTitle', { framework: fw.displayName }),
      html: t('Organizations.removeFrameworkHtml', { org: org.organizationName }),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: t('Organizations.removeFrameworkConfirm'),
      confirmButtonColor: '#ef4444',
    });
    if (!confirmed.isConfirmed) return;
    try {
      await axiosInstance.delete(`/admin/orgs/${org.id}/frameworks/${fw.id}`);
      await fetchOrganizations();
      setFwModal(prev => prev ? ({
        ...prev,
        org: { ...prev.org, frameworks: prev.org.frameworks.filter(f => f.id !== fw.id) }
      }) : null);
      Swal.fire({ toast: true, position: 'top-end', icon: 'success', title: t('Organizations.toastFrameworkRemoved'), showConfirmButton: false, timer: 2000 });
    } catch (e) {
      Swal.fire(t('Organizations.errorTitle'), e.response?.data?.message || t('Organizations.errorRemoveFramework'), 'error');
    }
  };

  const handleArchive = async (org) => {
    const c = await Swal.fire({
      title: `Archive ${org.organizationName}?`,
      html: 'The organization will be hidden from listings and all its users blocked from login. This is reversible — restore it any time from “Show archived”.',
      icon: 'warning', showCancelButton: true, confirmButtonText: 'Archive', confirmButtonColor: '#ef4444',
    });
    if (!c.isConfirmed) return;
    try {
      await axiosInstance.delete(`/admin/orgs/${org.id}`);
      await fetchOrganizations();
      Swal.fire({ toast: true, position: 'top-end', icon: 'success', title: 'Organization archived', showConfirmButton: false, timer: 2000 });
    } catch (e) {
      Swal.fire('Error', e.response?.data?.message || 'Failed to archive organization', 'error');
    }
  };

  // Peek: enter the org's account with the admin's own identity (60-min server-side session)
  const handlePeek = async (org) => {
    const c = await Swal.fire({
      title: `Enter ${org.organizationName}?`,
      text: 'You will browse their account as Platform Admin. Every action is logged in their activity trail.',
      input: 'text',
      inputPlaceholder: 'Reason (optional, audited)',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Enter org',
    });
    if (!c.isConfirmed) return;
    try {
      const res = await axiosInstance.post(`/admin/orgs/${org.id}/peek`, { reason: c.value || '' });
      startPeek(res.data);
      navigate('/dashboard');
    } catch (e) {
      const msg = e.response?.data?.message || 'Could not enter this organization.';
      Swal.fire({ title: t('Organizations.errorTitle'), text: String(msg), icon: 'error' });
    }
  };

  const handleRestore = async (org) => {
    try {
      await axiosInstance.post(`/admin/orgs/${org.id}/restore`);
      await fetchOrganizations();
      Swal.fire({ toast: true, position: 'top-end', icon: 'success', title: 'Organization restored', showConfirmButton: false, timer: 2000 });
    } catch (e) {
      Swal.fire('Error', e.response?.data?.message || 'Failed to restore organization', 'error');
    }
  };

  const handleToggleTestOrg = async (org) => {
    const enable = !org.testOrg;
    const c = await Swal.fire({
      title: enable
        ? t('Organizations.markTestOrgTitle', { org: org.organizationName })
        : t('Organizations.removeTestTagTitle', { org: org.organizationName }),
      text: enable ? t('Organizations.markTestOrgText') : t('Organizations.removeTestTagText'),
      icon: 'warning', showCancelButton: true,
      confirmButtonText: enable ? t('Organizations.markTestOrg') : t('Organizations.removeTestTag'),
      cancelButtonText: t('Organizations.cancelButton'),
    });
    if (!c.isConfirmed) return;
    try {
      await axiosInstance.post(`/admin/orgs/${org.id}/test-org`, { enabled: enable });
      await fetchOrganizations();
      Swal.fire({ toast: true, position: 'top-end', icon: 'success', showConfirmButton: false, timer: 2000,
        title: enable ? t('Organizations.toastTestOrgMarked') : t('Organizations.toastTestTagRemoved') });
    } catch (e) {
      Swal.fire(t('Organizations.errorTitle'), e.response?.data?.message || t('Organizations.errorTestOrg'), 'error');
    }
  };

  const openEmailModal = async (org) => {
    setEmailModal({ org, loading: true, sending: false, recipient: '', hasRecipient: false, subject: '', message: '' });
    try {
      const res = await axiosInstance.get(`/admin/orgs/${org.id}/follow-up-email/preview`);
      setEmailModal({ org, loading: false, sending: false,
        recipient: res.data.recipient || '', hasRecipient: !!res.data.hasRecipient,
        subject: res.data.subject || '', message: res.data.message || '' });
    } catch (e) {
      setEmailModal(null);
      Swal.fire('Error', e.response?.data?.message || 'Failed to load email preview', 'error');
    }
  };
  const closeEmailModal = () => setEmailModal(null);

  const sendFollowUp = async () => {
    if (!emailModal) return;
    setEmailModal(m => (m ? { ...m, sending: true } : m));
    try {
      const res = await axiosInstance.post(`/admin/orgs/${emailModal.org.id}/follow-up-email`, {
        subject: emailModal.subject, message: emailModal.message,
      });
      closeEmailModal();
      if (res.data.sent) fetchOrganizations();
      Swal.fire({ toast: true, position: 'top-end', icon: res.data.sent ? 'success' : 'warning',
        title: res.data.sent ? `Follow-up sent to ${res.data.recipient}` : 'Email was not accepted for delivery',
        showConfirmButton: false, timer: 2800 });
    } catch (e) {
      setEmailModal(m => (m ? { ...m, sending: false } : m));
      Swal.fire('Error', e.response?.data?.message || 'Failed to send follow-up email', 'error');
    }
  };

  const setField = (name, value) => {
    setForm(f => ({ ...f, [name]: value }));
    setFieldErr(e => ({ ...e, [name]: undefined }));
  };

  const validate = () => {
    const errs = {};
    if (!form.organizationName.trim()) errs.organizationName = t('Organizations.validationRequired');
    if (form.contactEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.contactEmail))
      errs.contactEmail = t('Organizations.validationInvalidEmail');
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) { setFieldErr(errs); return; }
    setSaving(true);
    try {
      const created = await axiosInstance.post('/organizations', {
        organizationName: form.organizationName.trim(),
        address:          form.address.trim(),
        orgWebsiteUrl:    form.orgWebsiteUrl.trim(),
        contactEmail:     form.contactEmail.trim(),
        contactNumber:    form.contactNumber.trim(),
        logoUrl:          form.logoUrl.trim() || null,
        frameworks:       form.frameworks.map(id => ({ id })),
      });
      // testOrg is deliberately NOT in the create payload: only the dedicated endpoint enforces the
      // platform-admin gate + Stripe-customer check and records who tagged the org (cf. ADM-12).
      if (form.testOrg) {
        try {
          await axiosInstance.post(`/admin/orgs/${created.data.id}/test-org`, { enabled: true });
        } catch (tagErr) {
          // The org exists now — close the form (a retry would hit "name already exists") and point
          // the admin at the row action instead of reporting the whole create as failed.
          closeModal();
          fetchOrganizations();
          Swal.fire({ title: t('Organizations.testOrgTagFailedTitle'), icon: 'warning',
            text: t('Organizations.testOrgTagFailedText', {
              message: tagErr.response?.data?.message || t('Organizations.errorTestOrg'),
            }) });
          return;
        }
      }
      closeModal();
      fetchOrganizations();
      Swal.fire({ title: t('Organizations.toastOrgCreated'), icon: 'success', confirmButtonText: t('Organizations.okButton') });
    } catch (err) {
      // 409 = name already taken (possibly by an archived org) — show it on the name field.
      const errorKey = err.response?.status === 409 ? err.response?.data?.errorKey : null;
      if (errorKey === 'orgNameExists' || errorKey === 'orgNameExistsArchived') {
        const name = form.organizationName.trim();
        setFieldErr(e => ({
          ...e,
          organizationName: errorKey === 'orgNameExistsArchived'
            ? t('Organizations.errorOrgNameExistsArchived', { name })
            : t('Organizations.errorOrgNameExists', { name }),
        }));
        return;
      }
      const msg = err.response?.data?.message || err.response?.data || t('Organizations.errorCreateOrg');
      Swal.fire({ title: t('Organizations.errorTitle'), text: String(msg), icon: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const filtered = organizations.filter(o => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (o.organizationName || '').toLowerCase().includes(q)
      || (o.contactEmail || '').toLowerCase().includes(q)
      || (o.orgWebsiteUrl || '').toLowerCase().includes(q);
  });

  return (
    <div className="org-page">
      {/* ── Header ── */}
      <div className="org-header">
        <div>
          <h2 className="org-title"><i className="fas fa-building" /> {t('Organizations.pageTitle')}</h2>
          <p className="org-subtitle">{t('Organizations.pageSubtitle')}</p>
        </div>
        <button className="org-add-btn" onClick={openModal}>
          <i className="fas fa-plus" /> {t('Organizations.addOrganization')}
        </button>
      </div>

      {/* ── Search ── */}
      <div className="org-search-row">
        <div className="org-search-wrap">
          <i className="fas fa-search org-search-icon" />
          <input className="org-search" placeholder={t('Organizations.searchPlaceholder')}
            value={search} onChange={e => setSearch(e.target.value)} />
          {search && (
            <button className="org-search-clear" onClick={() => setSearch('')}>
              <i className="fas fa-times" />
            </button>
          )}
        </div>
        <span className="org-count">{t('Organizations.organizationCount', { count: filtered.length })}</span>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#9ca3af', cursor: 'pointer', marginLeft: 14 }}>
          <input type="checkbox" checked={showArchived} onChange={e => setShowArchived(e.target.checked)} />
          Show archived
        </label>
      </div>

      {/* ── Table ── */}
      <div className="org-card">
        <div className="org-table-wrap">
          <table className="org-table">
            <thead>
              <tr>
                <th>{t('Organizations.colOrganization')}</th>
                <th>{t('Organizations.colContactEmail')}</th>
                <th>{t('Organizations.colWebsite')}</th>
                <th>{t('Organizations.colPhone')}</th>
                <th>{t('Organizations.colFrameworks')}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {orgsLoading ? (
                <tr>
                  <td colSpan={6} className="org-empty">
                    <i className="fas fa-spinner fa-spin" />
                    <p>{t('Organizations.loading')}</p>
                  </td>
                </tr>
              ) : orgsLoadError ? (
                <tr>
                  <td colSpan={6} className="org-empty">
                    <i className="fas fa-triangle-exclamation" />
                    <p>{t('Organizations.loadError')}</p>
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={6} className="org-empty">
                    <i className="fas fa-building" />
                    <p>{search ? t('Organizations.emptySearch') : t('Organizations.emptyNoOrgs')}</p>
                  </td>
                </tr>
              ) : filtered.map(org => (
                <tr key={org.id} className={`org-row${org.deleted ? ' org-row-archived' : ''}`}>
                  <td className="org-name-cell">
                    <OrgAvatar org={org} />
                    <div>
                      <div className="org-name">
                        {org.organizationName}
                        {org.deleted && (
                          <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 700, padding: '2px 7px', borderRadius: 4,
                            background: 'rgba(148,163,184,0.18)', color: '#94a3b8', textTransform: 'uppercase' }}>Archived</span>
                        )}
                        {org.followupSentAt && (
                          <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 700, padding: '2px 7px', borderRadius: 4,
                            background: 'rgba(16,185,129,0.15)', color: '#34d399', textTransform: 'uppercase' }}
                            title={`Follow-up sent ${new Date(org.followupSentAt).toLocaleString()}${org.followupCount > 1 ? ` (${org.followupCount}×)` : ''}${org.followupSentBy ? ` by ${org.followupSentBy}` : ''}`}>
                            <i className="fas fa-paper-plane" style={{ marginRight: 4 }} />Followed up
                          </span>
                        )}
                        {!org.deleted && !org.followupSentAt && org.contactEmail && (org.frameworks || []).length === 0 && (
                          <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 700, padding: '2px 7px', borderRadius: 4,
                            background: 'rgba(59,130,246,0.15)', color: '#60a5fa', textTransform: 'uppercase' }}
                            title="Signed up but not enrolled in any framework — a follow-up candidate">Prospect</span>
                        )}
                        {org.testOrg && (
                          <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 700, padding: '2px 7px', borderRadius: 4,
                            background: 'rgba(245,158,11,0.15)', color: '#fbbf24', textTransform: 'uppercase' }}
                            title={t('Organizations.testOrgBadgeTitle')}>
                            <i className="fas fa-flask" style={{ marginRight: 4 }} />{t('Organizations.testOrgBadge')}
                          </span>
                        )}
                      </div>
                      {org.address && <div className="org-address">{org.address}</div>}
                    </div>
                  </td>
                  <td className="org-email">{org.contactEmail || '—'}</td>
                  <td>
                    {org.orgWebsiteUrl
                      ? <a href={org.orgWebsiteUrl} target="_blank" rel="noreferrer" className="org-link">
                          {org.orgWebsiteUrl.replace(/^https?:\/\//, '')}
                        </a>
                      : '—'}
                  </td>
                  <td>{org.contactNumber || '—'}</td>
                  <td>
                    <div className="org-framework-tags">
                      {(org.frameworks || []).length === 0
                        ? <span className="org-no-fw">—</span>
                        : org.frameworks.map(f => (
                            <span key={f.id} className="org-fw-tag">{f.displayName}</span>
                          ))}
                    </div>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                      {!org.deleted && (
                        <button className="org-fw-manage-btn" onClick={() => handlePeek(org)}
                          title="Enter this organization's account as Platform Admin"
                          style={{ borderColor: 'rgba(168,85,247,0.35)', color: '#c084fc' }}>
                          <i className="fas fa-eye" /> Enter
                        </button>
                      )}
                      <button className="org-fw-manage-btn" onClick={() => openFwModal(org)} title={t('Organizations.manageFrameworksTitle')}>
                        <i className="fas fa-sitemap" /> {t('Organizations.frameworksButton')}
                      </button>
                      {!org.deleted && (
                        <button className="org-fw-manage-btn" onClick={() => openEmailModal(org)} title="Send a re-engagement email with your Calendly link"
                          style={{ borderColor: 'rgba(59,130,246,0.35)', color: '#60a5fa' }}>
                          <i className="fas fa-paper-plane" /> Follow-up
                        </button>
                      )}
                      {/* Untagging stays available on archived orgs so a stale exemption can be cleared. */}
                      {canTagTestOrg && (org.testOrg || !org.deleted) && (
                        <button className="org-fw-manage-btn" onClick={() => handleToggleTestOrg(org)}
                          title={org.testOrg ? t('Organizations.removeTestTagHint') : t('Organizations.markTestOrgHint')}
                          style={{ borderColor: 'rgba(245,158,11,0.35)', color: '#fbbf24' }}>
                          <i className="fas fa-flask" /> {org.testOrg ? t('Organizations.removeTestTag') : t('Organizations.markTestOrg')}
                        </button>
                      )}
                      {canDeleteOrg && (org.deleted ? (
                        <button className="org-fw-manage-btn" onClick={() => handleRestore(org)} title="Restore this organization"
                          style={{ borderColor: 'rgba(16,185,129,0.35)', color: '#34d399' }}>
                          <i className="fas fa-rotate-left" /> Restore
                        </button>
                      ) : (
                        <button className="org-fw-manage-btn" onClick={() => handleArchive(org)} title="Archive (soft-delete) this organization"
                          style={{ borderColor: 'rgba(239,68,68,0.35)', color: '#f87171' }}>
                          <i className="fas fa-box-archive" /> Archive
                        </button>
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Manage Frameworks Modal ── */}
      {fwModal && (
        <div className="org-overlay" onClick={closeFwModal}>
          <div className="org-modal" style={{ maxWidth: 520 }} onClick={e => e.stopPropagation()}>
            <div className="org-modal-header">
              <span><i className="fas fa-sitemap" /> {t('Organizations.fwModalHeader', { org: fwModal.org.organizationName })}</span>
              <button className="org-modal-close" onClick={closeFwModal}><i className="fas fa-times" /></button>
            </div>
            <div className="org-modal-body">
              <div className="org-section-label">{t('Organizations.enrolledFrameworks')}</div>
              {(fwModal.org.frameworks || []).length === 0 ? (
                <p style={{ color: '#9ca3af', fontSize: 13 }}>{t('Organizations.noFrameworksEnrolled')}</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {fwModal.org.frameworks.map(fw => (
                    <div key={fw.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
                      borderRadius: 8, padding: '8px 12px' }}>
                      <span style={{ color: '#e5e7eb', fontSize: 14 }}>
                        <i className="fas fa-check-circle" style={{ color: '#10b981', marginRight: 8 }} />
                        {fw.displayName}
                      </span>
                      <button
                        onClick={() => handleRemoveFramework(fwModal.org, fw)}
                        style={{ background: 'rgba(239,68,68,0.1)', color: '#f87171', border: '1px solid rgba(239,68,68,0.2)',
                          borderRadius: 6, padding: '4px 10px', fontSize: 12, cursor: 'pointer' }}>
                        <i className="fas fa-trash" /> {t('Organizations.removeButton')}
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div className="org-section-label" style={{ marginTop: 20 }}>{t('Organizations.addFramework')}</div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <div style={{ flex: 1 }}>
                  <Select
                    placeholder={t('Organizations.selectFrameworkPlaceholder')}
                    options={frameworks.filter(f =>
                      !(fwModal.org.frameworks || []).some(ef => ef.id === f.value)
                    )}
                    value={fwSelected}
                    onChange={(opt) => { setFwSelected(opt); setTscChecked({}); }}
                    className="org-select-container"
                    classNamePrefix="org-select"
                  />
                </div>
                <button className="org-btn-primary" onClick={handleAddFramework}
                  disabled={!fwSelected || fwAdding} style={{ whiteSpace: 'nowrap' }}>
                  {fwAdding ? <><i className="fas fa-spinner fa-spin" /> {t('Organizations.adding')}</> : <><i className="fas fa-plus" /> {t('Organizations.addButton')}</>}
                </button>
              </div>

              {fwSelectedIsSoc2 && (
                <div style={{ marginTop: 12 }}>
                  <div style={{ color: '#9ca3af', fontSize: 12, marginBottom: 8 }}>
                    {t('Organizations.tscSelectionHint', 'Trust Services Criteria (Security is mandatory; the rest are optional)')}
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(255,255,255,0.04)',
                      border: '1px solid rgba(255,255,255,0.08)', borderRadius: 6, padding: '6px 10px', fontSize: 13,
                      color: '#e5e7eb', opacity: 0.7 }}>
                      <input type="checkbox" checked disabled />
                      {t(TSC_LABEL_KEYS.CC)}
                    </label>
                    {SOC2_PILLARS.map(pl => (
                      <label key={pl.code} style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(255,255,255,0.04)',
                        border: '1px solid rgba(255,255,255,0.08)', borderRadius: 6, padding: '6px 10px', fontSize: 13, color: '#e5e7eb' }}>
                        <input type="checkbox" checked={!!tscChecked[pl.code]}
                          onChange={e => setTscChecked(prev => ({ ...prev, [pl.code]: e.target.checked }))} />
                        {t(pl.labelKey)}
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="org-modal-footer">
              <button className="org-btn-secondary" onClick={closeFwModal}>{t('Organizations.closeButton')}</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Add Organization Modal ── */}
      {showModal && (
        <div className="org-overlay" onClick={closeModal}>
          <div className="org-modal" onClick={e => e.stopPropagation()}>
            <div className="org-modal-header">
              <span><i className="fas fa-plus-circle" /> {t('Organizations.addOrganization')}</span>
              <button className="org-modal-close" onClick={closeModal}>
                <i className="fas fa-times" />
              </button>
            </div>

            <form className="org-modal-body" onSubmit={handleSubmit}>
              {/* ── Identity ── */}
              <div className="org-section-label">{t('Organizations.organizationDetails')}</div>
              <div className="org-grid-2">
                <div className="org-field org-field-full">
                  <label>{t('Organizations.labelOrganizationName')}</label>
                  <input className={`org-input ${fieldErr.organizationName ? 'org-input-err' : ''}`}
                    value={form.organizationName}
                    onChange={e => setField('organizationName', e.target.value)}
                    placeholder={t('Organizations.placeholderOrganizationName')}
                    autoFocus />
                  {fieldErr.organizationName && <span className="org-err">{fieldErr.organizationName}</span>}
                </div>

                <div className="org-field org-field-full">
                  <label>{t('Organizations.labelAddress')}</label>
                  <input className="org-input"
                    value={form.address}
                    onChange={e => setField('address', e.target.value)}
                    placeholder={t('Organizations.placeholderAddress')} />
                </div>

                <div className="org-field">
                  <label>{t('Organizations.labelContactEmail')}</label>
                  <input className={`org-input ${fieldErr.contactEmail ? 'org-input-err' : ''}`}
                    type="email"
                    value={form.contactEmail}
                    onChange={e => setField('contactEmail', e.target.value)}
                    placeholder="security@org.com" />
                  {fieldErr.contactEmail && <span className="org-err">{fieldErr.contactEmail}</span>}
                </div>

                <div className="org-field">
                  <label>{t('Organizations.labelContactNumber')}</label>
                  <input className="org-input"
                    value={form.contactNumber}
                    onChange={e => setField('contactNumber', e.target.value)}
                    placeholder="+1 555 000 0000" />
                </div>

                <div className="org-field">
                  <label>{t('Organizations.labelWebsiteUrl')}</label>
                  <input className="org-input"
                    value={form.orgWebsiteUrl}
                    onChange={e => setField('orgWebsiteUrl', e.target.value)}
                    placeholder="https://www.org.com" />
                </div>

                <div className="org-field">
                  <label>{t('Organizations.labelLogoUrl')} <span className="org-optional">{t('Organizations.optional')}</span></label>
                  <input className="org-input"
                    value={form.logoUrl}
                    onChange={e => setField('logoUrl', e.target.value)}
                    placeholder="https://cdn.org.com/logo.png" />
                </div>
              </div>

              {/* ── Frameworks ── */}
              <div className="org-section-label" style={{ marginTop: 20 }}>{t('Organizations.complianceFrameworks')}</div>
              <div className="org-field">
                <Select
                  isMulti
                  placeholder={t('Organizations.selectFrameworksPlaceholder')}
                  options={frameworks}
                  value={frameworks.filter(f => form.frameworks.includes(f.value))}
                  onChange={sel => setField('frameworks', sel ? sel.map(s => s.value) : [])}
                  className="org-select-container"
                  classNamePrefix="org-select"
                />
                <span className="org-field-note">
                  <i className="fas fa-info-circle" /> {t('Organizations.frameworksNote')}
                </span>
              </div>

              {canTagTestOrg && (
                <div className="org-field" style={{ marginTop: 20 }}>
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer', alignSelf: 'flex-start' }}>
                    <input type="checkbox" checked={form.testOrg}
                      onChange={e => setField('testOrg', e.target.checked)} />
                    {t('Organizations.labelTestOrg')}
                  </label>
                  <span className="org-field-note">
                    <i className="fas fa-info-circle" /> {t('Organizations.testOrgNote')}
                  </span>
                </div>
              )}
            </form>

            <div className="org-modal-footer">
              <button className="org-btn-secondary" onClick={closeModal} type="button">{t('Organizations.cancelButton')}</button>
              <button className="org-btn-primary" onClick={handleSubmit} disabled={saving} type="button">
                {saving
                  ? <><i className="fas fa-spinner fa-spin" /> {t('Organizations.creating')}</>
                  : <><i className="fas fa-plus" /> {t('Organizations.createOrganization')}</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Follow-up email modal ── */}
      {emailModal && (
        <div className="org-overlay" onClick={closeEmailModal}>
          <div className="org-modal" style={{ maxWidth: 560 }} onClick={e => e.stopPropagation()}>
            <div className="org-modal-header">
              <span><i className="fas fa-paper-plane" /> Follow-up email — {emailModal.org.organizationName}</span>
              <button className="org-modal-close" onClick={closeEmailModal}><i className="fas fa-times" /></button>
            </div>
            <div className="org-modal-body">
              {emailModal.loading ? (
                <p style={{ color: '#9ca3af', fontSize: 13 }}><i className="fas fa-spinner fa-spin" /> Loading…</p>
              ) : (
                <>
                  <div className="org-section-label">To</div>
                  {emailModal.hasRecipient ? (
                    <div style={{ color: '#e5e7eb', fontSize: 14, marginBottom: 4 }}>
                      <i className="fas fa-envelope" style={{ marginRight: 8, color: '#60a5fa' }} />{emailModal.recipient}
                    </div>
                  ) : (
                    <div style={{ color: '#f87171', fontSize: 13, marginBottom: 4 }}>
                      <i className="fas fa-triangle-exclamation" style={{ marginRight: 6 }} />
                      No contact email or user found for this organization — nothing to send to.
                    </div>
                  )}

                  <div className="org-section-label" style={{ marginTop: 16 }}>Subject</div>
                  <input className="org-search" style={{ width: '100%' }} value={emailModal.subject}
                    onChange={e => setEmailModal(m => ({ ...m, subject: e.target.value }))} />

                  <div className="org-section-label" style={{ marginTop: 16 }}>Message</div>
                  <textarea rows={9} value={emailModal.message}
                    onChange={e => setEmailModal(m => ({ ...m, message: e.target.value }))}
                    style={{ width: '100%', boxSizing: 'border-box', background: 'rgba(255,255,255,0.04)',
                      border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, padding: '10px 12px',
                      color: '#e5e7eb', fontSize: 14, lineHeight: 1.5, resize: 'vertical', fontFamily: 'inherit' }} />
                  <span className="org-field-note" style={{ display: 'block', marginTop: 8 }}>
                    <i className="fas fa-info-circle" /> A branded “Book a time →” button (your Calendly link) and signature are appended automatically below your message.
                  </span>
                </>
              )}
            </div>
            <div className="org-modal-footer">
              <button className="org-btn-secondary" onClick={closeEmailModal} type="button">Cancel</button>
              <button className="org-btn-primary" onClick={sendFollowUp} type="button"
                disabled={emailModal.loading || emailModal.sending || !emailModal.hasRecipient}>
                {emailModal.sending
                  ? <><i className="fas fa-spinner fa-spin" /> Sending…</>
                  : <><i className="fas fa-paper-plane" /> Send follow-up</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Organizations;
